/*
 * Otter Goods storefront behaviour (layout/og.liquid).
 *
 * Entry tracking ("as if the site only sells this product"):
 *   data-og="entry"   product pages: remember the page the visitor arrived on in cookie og_entry.
 *                     A new external arrival (or no cookie yet) overwrites it, so the latest link
 *                     wins; internal navigation never does. UTMs/referrer go to cart attributes so
 *                     they end up on the order.
 *   data-og="home"    root URL: the full storefront (sticky nav, floating hero cards). No redirects:
 *                     the home page and the catalog are open to everyone (owner, 2026-10-31).
 *
 * Product page: variant picker, gallery, quantity, delivery estimate, sticky bar, size finder and
 * a one-tap "Buy now" that checks out only this item.
 */
(function () {
  var ENTRY = 'og_entry'; // kept for attribution (first and latest landing page), no longer used to redirect
  var FIRST = 'og_first';
  var ATTR_KEY = 'og_attr';
  var MAX_AGE = 60 * 60 * 24 * 90;
  var secure = location.protocol === 'https:' ? ';Secure' : '';
  var root = document.documentElement;
  root.classList.add('og-js');

  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + ';path=/;max-age=' + MAX_AGE + ';SameSite=Lax' + secure;
  }
  // Only same-site absolute paths, never "//host" or schemes (no open redirect through the cookie).
  function safePath(p) {
    return typeof p === 'string' && p.charAt(0) === '/' && p.charAt(1) !== '/' && p.indexOf('\\') === -1 ? p : null;
  }
  function externalArrival() {
    if (!document.referrer) return true;
    try { return new URL(document.referrer).host !== location.host; } catch (e) { return true; }
  }
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status);
      return r.json();
    });
  }
  function store(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {} }
  function recall(key) { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; } }

  var mode = root.getAttribute('data-og');

  /* ── Entry tracking ─────────────────────────────────────────────── */
  if (mode === 'entry') {
    var fresh = externalArrival();
    if (fresh || !safePath(getCookie(ENTRY))) setCookie(ENTRY, location.pathname);
    if (!getCookie(FIRST)) setCookie(FIRST, location.pathname);

    if (fresh) {
      var q = new URLSearchParams(location.search);
      var attrs = { og_landing: location.pathname };
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ttclid'].forEach(function (k) {
        if (q.get(k)) attrs[k] = q.get(k).slice(0, 200);
      });
      if (document.referrer) attrs.og_referrer = document.referrer.slice(0, 300);
      store(ATTR_KEY, attrs);
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
          keepalive: true
        });
      } catch (e) { /* attribution is best effort */ }
    }
  }

  /* ── Home: sticky nav state and the floating hero cards ─────────── */
  if (mode === 'home') {
    var nav = document.querySelector('[data-og-nav]');
    var stage = document.querySelector('[data-og-stage]');
    var floats = stage ? Array.prototype.slice.call(stage.querySelectorAll('[data-og-float]')) : [];
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
    // Each card moves at its own depth, so the stack separates as you scroll and leans toward the pointer.
    var depth = [26, 52, 78];
    var px = 0, py = 0, ticking = false;
    var paint = function () {
      ticking = false;
      if (nav) nav.classList.toggle('is-scrolled', window.scrollY > 8);
      if (still || !stage) return;
      var r = stage.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      var p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight));
      floats.forEach(function (el, i) {
        var d = depth[i] || 30;
        el.style.transform = 'translate3d(' + (px * d * 0.35).toFixed(1) + 'px,' + (p * d + py * d * 0.25).toFixed(1) + 'px,0)';
      });
    };
    var ask = function () { if (!ticking) { ticking = true; requestAnimationFrame(paint); } };
    window.addEventListener('scroll', ask, { passive: true });
    window.addEventListener('resize', ask, { passive: true });
    if (fine && !still && stage) {
      var hero = stage.closest('section') || stage;
      hero.addEventListener('pointermove', function (e) {
        var b = hero.getBoundingClientRect();
        px = (e.clientX - b.left) / b.width - 0.5;
        py = (e.clientY - b.top) / b.height - 0.5;
        ask();
      });
      hero.addEventListener('pointerleave', function () { px = 0; py = 0; ask(); });
    }
    paint();
  }

  /* ── Reveal on scroll ───────────────────────────────────────────── */
  var reveals = document.querySelectorAll('.og-reveal');
  if (reveals.length) {
    if ('IntersectionObserver' in window) {
      var ro = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); ro.unobserve(e.target); } });
      }, { rootMargin: '0px 0px 12% 0px' }); // start just before a section scrolls into view
      reveals.forEach(function (el) { ro.observe(el); });
    } else {
      reveals.forEach(function (el) { el.classList.add('is-in'); });
    }
  }

  /* ── Product page ───────────────────────────────────────────────── */
  var dataEl = document.querySelector('script[data-og-product]');
  if (!dataEl) return;
  var data;
  try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var form = document.querySelector('[data-og-form]');
  var idInput = form && form.querySelector('[data-og-variant-id]');
  var qtyInput = form && form.querySelector('[data-og-qty-input]');
  var submit = form && form.querySelector('[data-og-submit]');
  var errorEl = form && form.querySelector('[data-og-error]');

  /* Gallery */
  var track = document.querySelector('[data-og-track]');
  var slides = track ? track.querySelectorAll('[data-og-slide]') : [];
  var thumbs = document.querySelectorAll('[data-og-thumb]');
  var countEl = document.querySelector('[data-og-count]');
  var prevBtn = document.querySelector('[data-og-prev]');
  var nextBtn = document.querySelector('[data-og-next]');
  var current = 0;

  function setActive(i) {
    current = i;
    thumbs.forEach(function (t, n) {
      var on = n === i;
      t.classList.toggle('is-on', on);
      if (on) { t.setAttribute('aria-current', 'true'); } else { t.removeAttribute('aria-current'); }
    });
    if (countEl) countEl.textContent = String(i + 1);
    if (prevBtn) prevBtn.disabled = i === 0;
    if (nextBtn) nextBtn.disabled = i === slides.length - 1;
  }
  // Photos 2+ are rendered with data-og-src/srcset so the first photo gets the bandwidth. Load them
  // once the page has finished loading, or straight away when the shopper starts browsing photos.
  var deferredLoaded = false;
  function loadDeferred() {
    if (deferredLoaded) return;
    deferredLoaded = true;
    document.querySelectorAll('img[data-og-src]').forEach(function (img) {
      if (img.getAttribute('data-og-srcset')) img.setAttribute('srcset', img.getAttribute('data-og-srcset'));
      img.src = img.getAttribute('data-og-src');
      img.removeAttribute('data-og-src');
      img.removeAttribute('data-og-srcset');
    });
  }
  if (document.readyState === 'complete') { setTimeout(loadDeferred, 300); }
  else { window.addEventListener('load', function () { setTimeout(loadDeferred, 300); }); }
  // Jumps of more than one photo (thumbnail clicks, colour changes) cut straight to the photo with a
  // short fade instead of sliding past every photo in between. Neighbours (arrows, keys) still slide.
  var settling = null;
  function goTo(i) {
    if (!track || !slides[i] || i === current) { setActive(i); return; }
    loadDeferred();
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var slide = Math.abs(i - current) === 1 && !reduce;
    clearTimeout(settling);
    settling = setTimeout(function () { settling = null; }, slide ? 600 : 80); // ignore scroll events we caused
    track.scrollTo({ left: slides[i].offsetLeft, behavior: slide ? 'smooth' : 'auto' });
    if (!slide && !reduce) {
      var img = slides[i].querySelector('img');
      if (img) { img.classList.remove('og-gal__img--in'); void img.offsetWidth; img.classList.add('og-gal__img--in'); }
    }
    setActive(i);
  }
  if (track && slides.length) {
    var ticking = false;
    track.addEventListener('pointerdown', loadDeferred, { passive: true });
    track.addEventListener('touchstart', loadDeferred, { passive: true });
    track.addEventListener('scroll', function () {
      loadDeferred();
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (settling) return;
        var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        if (i !== current) setActive(Math.min(slides.length - 1, Math.max(0, i)));
      });
    }, { passive: true });
    thumbs.forEach(function (t) { t.addEventListener('click', function () { goTo(Number(t.getAttribute('data-og-thumb'))); }); });
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(Math.max(0, current - 1)); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(Math.min(slides.length - 1, current + 1)); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, current + 1)); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, current - 1)); }
    });
    setActive(0);
  }

  /* Page videos: muted loop, load and play only while on screen. */
  if ('IntersectionObserver' in window) {
    var vidIO = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (e.isIntersecting && e.intersectionRatio > 0.4) { var p = e.target.play(); if (p && p.catch) p.catch(function () {}); }
        else { e.target.pause(); }
      });
    }, { threshold: [0, 0.4, 0.8] });
    document.querySelectorAll('[data-og-vid]').forEach(function (v) { vidIO.observe(v); });
  }

  /* Multi-buy offer ("buy X, get Y"). Mirrors this product's Shopify automatic discount: for every buy+get units,
     `get` of them are reduced by `percent`, each reduction rounded down to the cent like checkout. */
  var offer = data.offer || null;
  var fmt$ = new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' });
  var currentVariant = null;
  function totalCents(unit, q) {
    var full = unit * q;
    if (!offer) return full;
    if (offer.mid && q === 2) return full - Math.floor(unit * offer.mid.percent / 100);
    var reduced = Math.floor(q / (offer.buy + offer.get)) * offer.get;
    return full - reduced * Math.floor(unit * offer.percent / 100);
  }
  function updateTotals() {
    var v = currentVariant || data.variants[0];
    if (!v || typeof v.priceCents !== 'number') return;
    var q = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    var full = v.priceCents * q, tot = totalCents(v.priceCents, q);
    var cta = document.querySelector('[data-og-cta-total]');
    var was = document.querySelector('[data-og-cta-was]');
    if (cta) cta.textContent = fmt$.format(tot / 100);
    if (was) { was.hidden = tot === full; was.textContent = fmt$.format(full / 100); }
    document.querySelectorAll('[data-og-tier-total]').forEach(function (el) {
      var n = Number(el.getAttribute('data-og-tier-total')); el.textContent = fmt$.format(totalCents(v.priceCents, n) / 100);
    });
    document.querySelectorAll('[data-og-tier-was]').forEach(function (el) {
      el.textContent = fmt$.format(v.priceCents * Number(el.getAttribute('data-og-tier-was')) / 100);
    });
    document.querySelectorAll('[data-og-summary-total]').forEach(function (el) {
      el.textContent = fmt$.format(tot / 100);
    });
    document.querySelectorAll('[data-og-summary-quantity]').forEach(function (el) {
      el.hidden = q === 1;
      el.textContent = q > 1 ? data.quantityLabel + ': ' + q + ' · ' : '';
    });
  }
  document.querySelectorAll('input[name="og-tier"]').forEach(function (r) {
    r.addEventListener('change', function () { if (qtyInput) qtyInput.value = r.value; updateTotals(); });
  });

  /* Variant picker */
  var fieldsets = document.querySelectorAll('[data-og-option]');
  function selectedOptions() {
    var out = [];
    fieldsets.forEach(function (fs) {
      var checked = fs.querySelector('input:checked');
      out[Number(fs.getAttribute('data-og-option'))] = checked ? checked.value : null;
    });
    return out;
  }
  function findVariant(opts) {
    for (var i = 0; i < data.variants.length; i++) {
      var v = data.variants[i];
      var ok = true;
      for (var k = 0; k < opts.length; k++) if (v.options[k] !== opts[k]) { ok = false; break; }
      if (ok) return v;
    }
    return null;
  }
  // The first option the shopper still has to choose (e.g. Size), or null.
  function missingOption() {
    for (var i = 0; i < fieldsets.length; i++) if (!fieldsets[i].querySelector('input:checked')) return fieldsets[i];
    return null;
  }
  function applyVariant(v, fromUser) {
    var opts = selectedOptions();
    opts.forEach(function (val, i) {
      var label = document.querySelector('[data-og-option-value="' + i + '"]');
      if (label) label.textContent = val || 'Choose one';
    });
    var missing = missingOption();
    if (missing) {
      // Keep the button live: pressing it points the shopper at the option they skipped.
      var name = (missing.getAttribute('data-og-option-name') || 'option').toLowerCase();
      if (submit) { submit.disabled = false; submit.classList.add('is-need'); setLabel('Select a ' + name); }
      document.querySelectorAll('[data-og-endcap-variant], [data-og-sticky-variant]').forEach(function (el) { el.textContent = opts.filter(Boolean).join(' / '); });
      document.querySelectorAll('[data-og-buy-proxy] .og-cta__label, [data-og-sticky] [data-og-buy-proxy]').forEach(function (el) { if (!el.children.length) el.textContent = 'Choose ' + name; });
      if (fromUser) {
        // Still show the colour they picked.
        var near = data.variants.filter(function (x) { return opts.every(function (o, k) { return o === null || x.options[k] === o; }); })[0];
        if (near && typeof near.media === 'number') goTo(near.media);
        if (near && near.image) document.querySelectorAll('[data-og-variant-image]').forEach(function (img) { img.loading = 'eager'; img.removeAttribute('srcset'); img.src = near.image; });
      }
      return;
    }
    fieldsets.forEach(function (fs) { fs.classList.remove('is-missing'); });
    if (submit) submit.classList.remove('is-need');
    document.querySelectorAll('[data-og-buy-proxy] .og-cta__label, [data-og-sticky] [data-og-buy-proxy]').forEach(function (el) { if (!el.children.length) el.textContent = 'Buy now'; });
    if (!v) {
      if (submit) { submit.disabled = true; setLabel('Unavailable'); }
      return;
    }
    if (idInput) idInput.value = v.id;
    document.querySelectorAll('[data-og-price]').forEach(function (el) { el.textContent = v.price; });
    currentVariant = v;
    if (typeof updateTotals === 'function') updateTotals();
    document.querySelectorAll('[data-og-endcap-variant], [data-og-sticky-variant]').forEach(function (el) { el.textContent = v.title; });
    if (submit) { submit.disabled = !v.available; setLabel(v.available ? 'Buy now' : 'Sold out'); }
    if (fromUser) {
      // The server already rendered the right image for the initial variant; only swap on a change.
      if (v.image) document.querySelectorAll('[data-og-variant-image]').forEach(function (img) { img.loading = 'eager'; img.removeAttribute('srcset'); img.src = v.image; });
      if (typeof v.media === 'number') goTo(v.media);
      try { var u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(null, '', u.toString()); } catch (e) {}
    }
  }
  function setLabel(text) {
    var l = submit && submit.querySelector('[data-og-cta-label]');
    if (l) l.textContent = text;
  }
  fieldsets.forEach(function (fs) {
    fs.addEventListener('change', function () { applyVariant(findVariant(selectedOptions()), true); });
  });
  if (fieldsets.length) applyVariant(findVariant(selectedOptions()), false);
  updateTotals();

  /* Quantity */
  document.querySelectorAll('[data-og-qty]').forEach(function (b) {
    b.addEventListener('click', function () {
      if (!qtyInput) return;
      var n = Math.min(10, Math.max(1, (parseInt(qtyInput.value, 10) || 1) + Number(b.getAttribute('data-og-qty'))));
      qtyInput.value = String(n);
      updateTotals();
    });
  });
  if (qtyInput) qtyInput.addEventListener('change', function () {
    qtyInput.value = String(Math.min(10, Math.max(1, parseInt(qtyInput.value, 10) || 1)));
    updateTotals();
  });

  /* Custom pixel events (engagement). fbq exists only when the visitor's cookie consent allows marketing
     (snippets/og-pixel.liquid). Each event fires once per page view. */
  var sentEvents = {};
  // Checked again at the moment of sending, so a visitor who declines cookies never leaks an event even if the pixel's
  // own consent switch is slow to flip.
  function marketingOk() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    return !!(window.fbq && cp && cp.marketingAllowed && cp.marketingAllowed());
  }
  function ping(name) {
    if (sentEvents[name] || !marketingOk()) return;
    sentEvents[name] = true;
    window.fbq('trackCustom', name);
  }

  /* Meta pixel (loaded by snippets/og-pixel.liquid): AddToCart and InitiateCheckout, with the discounted line total.
     Waits a moment so the browser has queued the requests before the page is replaced by checkout. */
  function trackBuy(res, qty) {
    if (!marketingOk()) return null;
    try {
      var line = res && res.items && res.items[0];
      var params = {
        content_type: 'product',
        content_ids: [String(line ? line.product_id : '')],
        content_name: line ? line.product_title : '',
        num_items: qty,
        value: line ? line.final_line_price / 100 : 0,
        currency: data.currency
      };
      window.fbq('track', 'AddToCart', params);
      window.fbq('track', 'InitiateCheckout', params);
    } catch (e) { return null; }
    return new Promise(function (done) { setTimeout(done, 200); });
  }

  /* Buy now: check out only this item. Falls back to the plain form (cart/add + return_to=/checkout). */
  var busy = false;
  if (form) {
    form.addEventListener('submit', function (e) {
      var missing = missingOption();
      if (missing) {
        ping('TappedBuyNoSize'); // wanted to buy but had not chosen an option yet
        e.preventDefault();
        missing.classList.remove('is-missing');
        void missing.offsetWidth; // restart the nudge animation
        missing.classList.add('is-missing');
        missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!window.fetch || busy) return;
      e.preventDefault();
      busy = true;
      if (submit) submit.classList.add('is-busy');
      if (errorEl) errorEl.hidden = true;
      var id = Number(idInput && idInput.value);
      var quantity = Math.min(10, Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1));
      var added = null;
      postJSON('/cart/clear.js')
        .then(function () { return postJSON('/cart/add.js', { items: [{ id: id, quantity: quantity }] }); })
        .then(function (res) {
          added = res;
          var a = recall(ATTR_KEY);
          return a ? postJSON('/cart/update.js', { attributes: a }).catch(function () {}) : null;
        })
        .then(function () { return trackBuy(added, quantity); })
        .then(function () { location.assign('/checkout'); })
        .catch(function () {
          busy = false;
          if (submit) submit.classList.remove('is-busy');
          HTMLFormElement.prototype.submit.call(form);
        });
    });
  }
  // Coming back from checkout with the Back button restores this page from the back/forward cache,
  // spinner and all. Reset the button so it can be pressed again.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    busy = false;
    if (submit) submit.classList.remove('is-busy');
  });
  document.querySelectorAll('[data-og-buy-proxy]').forEach(function (b) {
    b.addEventListener('click', function () {
      if (!form) return;
      if (form.requestSubmit) { form.requestSubmit(submit || undefined); } else { HTMLFormElement.prototype.submit.call(form); }
    });
  });

  /* Delivery estimate (business days, local time) */
  function addBusinessDays(d, n) {
    var x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    while (n > 0) { x.setDate(x.getDate() + 1); var w = x.getDay(); if (w !== 0 && w !== 6) n--; }
    return x;
  }
  var ship = data.ship;
  var etaRange = document.querySelector('[data-og-eta-range]');
  if (ship && ship.processing && ship.transit && etaRange) {
    var now = new Date();
    var fmt = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    var lo = addBusinessDays(now, ship.processing[0] + ship.transit[0]);
    var hi = addBusinessDays(now, ship.processing[1] + ship.transit[1]);
    etaRange.textContent = fmt.format(lo) + ' – ' + fmt.format(hi);

    // Christmas cut-off: latest order day whose slowest estimate still lands by Dec 23.
    var xmasEl = document.querySelector('[data-og-xmas]');
    var month = now.getMonth();
    if (xmasEl && (month === 9 || month === 10 || month === 11)) {
      var deadline = new Date(now.getFullYear(), 11, 23);
      var maxDays = ship.processing[1] + ship.transit[1];
      var cutoff = new Date(deadline);
      var guard = 0;
      while (addBusinessDays(cutoff, maxDays) > deadline && guard++ < 60) cutoff.setDate(cutoff.getDate() - 1);
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (today <= cutoff) {
        xmasEl.innerHTML = '';
        xmasEl.appendChild(document.createTextNode('Gifting? Order by '));
        var s = document.createElement('strong');
        s.textContent = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(cutoff);
        xmasEl.appendChild(s);
        xmasEl.appendChild(document.createTextNode(' to have it before Christmas.'));
        xmasEl.hidden = false;
      }
    }
  }

  /* Sticky bar: visible whenever the main button is off screen (including on arrival, so a phone shopper sees the
     price and a button on the first screen), hidden while the main button or the closing panel is on screen. */
  var sticky = document.querySelector('[data-og-sticky]');
  var endcap = document.querySelector('[data-og-endcap]');
  if (sticky && submit && 'IntersectionObserver' in window) {
    var ctaVisible = true, endVisible = false;
    var update = function () {
      var on = !ctaVisible && !endVisible;
      sticky.classList.toggle('is-on', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      var b = sticky.querySelector('button');
      if (b) b.tabIndex = on ? 0 : -1;
    };
    new IntersectionObserver(function (es) { ctaVisible = es[0].isIntersecting; update(); }).observe(submit);
    if (endcap) new IntersectionObserver(function (es) { endVisible = es[0].isIntersecting; update(); }).observe(endcap);
  }

  /* Size guide + finder */
  var dialog = document.querySelector('[data-og-size-dialog]');
  if (dialog) {
    document.querySelectorAll('[data-og-open-size]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (dialog.showModal) { dialog.showModal(); } else { dialog.setAttribute('open', ''); }
        var c = dialog.querySelector('[data-og-chest]');
        if (c) setTimeout(function () { c.focus(); }, 50);
      });
    });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) dialog.close(); });
    var chest = dialog.querySelector('[data-og-chest]');
    var result = dialog.querySelector('[data-og-fit-result]');
    var find = function () {
      if (!chest || !result || !data.fit) return;
      var n = parseFloat(chest.value);
      dialog.querySelectorAll('[data-og-size-row]').forEach(function (r) { r.classList.remove('is-match'); });
      result.textContent = '';
      if (!n || n < 28 || n > 70) { result.textContent = 'Enter your chest measurement in inches, for example 40.'; return; }
      var match = null;
      for (var i = 0; i < data.fit.length; i++) if (n <= data.fit[i].maxChest) { match = data.fit[i]; break; }
      if (!match) {
        var last = data.fit[data.fit.length - 1];
        result.textContent = 'This jacket fits chests up to ' + last.maxChest + ' in, so it would be tight on you. We would rather tell you now.';
        return;
      }
      var row = dialog.querySelector('[data-og-size-row="' + match.size + '"]');
      if (row) row.classList.add('is-match');
      result.appendChild(document.createTextNode('We recommend '));
      var strong = document.createElement('strong');
      strong.textContent = 'US ' + match.size;
      result.appendChild(strong);
      result.appendChild(document.createTextNode('.'));
      var pick = document.createElement('button');
      pick.type = 'button';
      pick.className = 'og-link';
      pick.textContent = 'Select ' + match.size;
      pick.addEventListener('click', function () {
        var input = document.querySelector('.og-opt--size input[value="' + match.size + '"]');
        if (input) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
        dialog.close();
      });
      result.appendChild(pick);
    };
    var findBtn = dialog.querySelector('[data-og-find]');
    if (findBtn) findBtn.addEventListener('click', find);
    if (chest) chest.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); find(); } });
  }

  /* Engagement events for the Meta pixel, so Events Manager shows how far visitors get on a product page (Shopify only
     counts time between two tracked actions, so a visitor who reads for a minute without tapping shows as 0 seconds). */
  (function () {
    var seconds = 0;
    setInterval(function () {
      if (document.visibilityState !== 'visible') return;
      seconds++;
      if (seconds === 15) ping('Stayed15s');
      if (seconds === 45) ping('Stayed45s');
    }, 1000);
    var queued = false;
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        var doc = document.documentElement;
        var seen = (window.pageYOffset + window.innerHeight) / Math.max(1, doc.scrollHeight);
        if (seen >= 0.5) ping('Scrolled50');
        if (seen >= 0.9) ping('Scrolled90');
      });
    }, { passive: true });
    if (form) form.addEventListener('change', function () { ping('PickedOption'); });
    document.querySelectorAll('[data-og-open-size]').forEach(function (b) { b.addEventListener('click', function () { ping('OpenedSizeGuide'); }); });
  })();
})();
